import { Check, Loader2, Send, RefreshCw, ArrowRight } from "lucide-react";
import { V } from "@/lib/vocab";

/* Invited → Setting up → Ready → Confirmed → Started → Done.
   A company already on the community's list skips the first two nodes (iter371). */
const NODES = [["invited", "Invited"], ["setting_up", "Setting up"], ["ready", "Ready"],
  ["confirmed", "Confirmed"], ["started", "Started"], ["done", "Done"]];
const ORDER = { unassigned: -1, invited: 0, setting_up: 1, needs_approval: 1.5, paperwork: 1.6, ready: 2, confirmed: 3, started: 4, done: 5, cancelled: -1 };
const PAPER = { coi: "insurance certificate", w9: "W-9", agreement: "signed agreement" };
const when = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export function JobSequence({ wo, busy, readOnly, onSendNow, onRecheck, onApprove }) {
  const vs = wo.vendor_stage || {};
  const stage = vs.stage || "unassigned";
  if (stage === "unassigned" || stage === "cancelled") return null;
  const nodes = vs.path === "invite" ? NODES : NODES.slice(2);
  const pos = ORDER[stage] ?? -1;
  const v = wo.vendor_name || "The company";
  const missing = (vs.missing || []).filter((k) => k !== "vendor").map((k) => PAPER[k] || k).join(" and ");

  const line = {
    invited: vs.held
      ? <>Invite to <b>{v}</b> goes out <b>{vs.held.send_at_local}</b> — it's after hours at the {V.lc("org")}.</>
      : <>Invite sent to <b>{v}</b>{vs.invite_sent_at ? ` · ${when(vs.invite_sent_at)}` : ""}. Waiting for them to start their setup — you'll hear when they finish.</>,
    setting_up: <><b>{v}</b> has started their setup. You'll hear the moment they finish.</>,
    needs_approval: <><b>{v}</b> finished setup — approve them for this {V.lc("org")} and the job moves to Ready.</>,
    paperwork: <>Approved · waiting on <b>{v}</b>'s {missing || "paperwork"} — they've been asked. Nobody is sent to the property until it's in.{wo.paperwork_due && <> They said by <b>{String(wo.paperwork_due).slice(0, 10)}</b>.</>}</>,
    ready: <><b>{v}</b> is ready — paperwork on file. Confirm the job to set the price, start and contract.</>,
    confirmed: <>Confirmed{wo.start_window ? <> · starts <b>{wo.start_window}</b></> : ""}. Waiting on {v}'s crew to start.</>,
    started: <><b>{v}</b> is on it.</>,
    done: <>Done{wo.completed_at ? ` · ${when(wo.completed_at)}` : ""}.</>,
  }[stage];

  return (
    <div data-testid="wo-sequence" data-stage={stage} className="rounded-2xl border border-sand-200 bg-white px-4 py-3">
      <ol className="flex flex-wrap items-center gap-y-1.5">
        {nodes.map(([k, label], i) => {
          const o = ORDER[k];
          const done = pos > o || (stage === "done" && k === "done");
          const cur = !done && (pos === o || (o === 2 && (stage === "needs_approval" || stage === "paperwork")) );
          return (
            <li key={k} data-testid={`wo-seq-${k}`} data-state={done ? "done" : cur ? "current" : "todo"} className="flex items-center">
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                done ? "bg-emerald-50 text-emerald-700" : cur ? "bg-ink text-white" : "bg-sand-100 text-ink/40"}`}>
                {done && <Check className="h-3 w-3" />}{label}
              </span>
              {i < nodes.length - 1 && <ArrowRight className="mx-1 h-3 w-3 text-ink/25" />}
            </li>
          );
        })}
      </ol>
      <div data-testid="wo-seq-line" className={`mt-2 text-xs leading-relaxed ${stage === "paperwork" ? "text-amber-900" : "text-ink/65"}`}>{line}</div>
      {!readOnly && (
        <div className="mt-2 flex flex-wrap gap-2">
          {stage === "invited" && vs.held && (
            <button data-testid="wo-seq-send-now" disabled={busy} onClick={onSendNow}
              className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send it now
            </button>
          )}
          {stage === "needs_approval" && (
            <button data-testid="wo-seq-approve" onClick={onApprove}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white">
              Review &amp; approve {v}
            </button>
          )}
          {(stage === "paperwork" || stage === "setting_up") && (
            <button data-testid="wo-recheck" disabled={busy} onClick={onRecheck}
              className="inline-flex items-center gap-1 rounded-lg border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/60 disabled:opacity-40">
              <RefreshCw className="h-3 w-3" /> Check again
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* After the job is confirmed: who got the details, and a way to send them again. */
export function ScheduleEmailLine({ wo, busy, readOnly, onSend }) {
  const se = wo.schedule_email;
  return (
    <div data-testid="wo-schedule-email" className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-sand-50 px-4 py-2.5 text-xs text-ink/65">
      <span className="min-w-[12rem] flex-1">
        {se?.sent
          ? <>Details emailed {when(se.at)} to <b>{(se.to || []).join(", ")}</b>{(se.cc || []).length ? <> · cc {(se.cc || []).join(", ")}</> : ""}.</>
          : se?.at
            ? <>Details addressed {when(se.at)} to <b>{(se.to || []).join(", ")}</b>{(se.cc || []).length ? <> · cc {(se.cc || []).join(", ")}</> : ""} — {se.why || "not delivered"}.</>
            : <>Nobody has been emailed the details of this job yet.</>}
      </span>
      {!readOnly && (
        <button data-testid="wo-schedule-email-send" disabled={busy} onClick={onSend}
          className="inline-flex items-center gap-1 rounded-lg border border-sand-300 bg-white px-3 py-1.5 font-bold text-ink/70 disabled:opacity-40">
          <Send className="h-3 w-3" /> {se?.at ? "Send again" : "Email the details"}
        </button>
      )}
    </div>
  );
}
